import { useState, useEffect } from 'react';
import { ITutorData } from '../../hexagonal-architecture-frontend-base/src/domain/models/ITutor';
import { type TutorInfoErrors } from '../../hexagonal-architecture-frontend-base/src/domain/models/User';
import { validate } from '../../hexagonal-architecture-frontend-base/src/domain/services/user.service';
import { useSession, useSetPopup, useShowMobileVerificationOpup, useUpdateUser } from '../';

export const useEditProfile = () => {
  const { userInfo: user } = useSession();
  const { handleUpdateUser } = useUpdateUser();
  const [isEditing, setIsEditing] = useState(false);
  const { showMobileVerificationPopup } = useShowMobileVerificationOpup();
  const { closePopup } = useSetPopup();
  // Setter => no return
  const [isErrorInFields, setIsErrorInFields] = useState<boolean>(true);
  // Value => no return & Setter => no return
  const [userBackup, setUserBackup] = useState<ITutorData>({
    tutorFullName: user?.tutorFullName ?? '',
    tutorCountryId: user?.tutorCountryId ?? '',
    phone: user?.phone ?? '',
    email: user?.email ?? '',
  });
  // Value => no return & Setter => no return
  const [userEdit, setUserEdit] = useState<ITutorData>({
    tutorFullName: user?.tutorFullName ?? null,
    tutorCountryId: user?.tutorCountryId ?? null,
    phone: user?.phone ?? '',
    email: user?.email ?? '',
  });
  // Setter => no return
  const [errorTutorMessage, setErrorTutorMessage] = useState<TutorInfoErrors>({
    tutorFullNameErr: '',
    tutorCountryIdErr: '',
    emailErr: '',
  });
  // Setter => no return

  useEffect(() => {
    if (user == null) return;

    const userInfo: ITutorData = {
      tutorFullName: user.tutorFullName,
      tutorCountryId: user.tutorCountryId,
      phone: user.phone ?? '',
      email: user.email,
    };

    setUserBackup(userInfo);
    setUserEdit(userInfo);
  }, [user]);

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setUserEdit(userBackup);
    setErrorTutorMessage({
      tutorFullNameErr: '',
      tutorCountryIdErr: '',
      emailErr: '',
    });
  };

  const handleUpdateUserInfo = async () => {
    // setIsEditing(false);
    if (user == null) return;

    const newTutorCountryId = userEdit.tutorCountryId ?? null;
    const newTutorFullName = userEdit.tutorFullName ?? null;
    const newPhone = userEdit.phone ?? '';
    const newEmail = userEdit.email ?? null;

    const updateUser = async (smsCode?: string) => {
      await handleUpdateUser(
        { newTutorCountryId, newTutorFullName, newPhone, newEmail, smsCode },
        {
          onSuccess: () => {
            setIsErrorInFields(true);
            setIsEditing(false);
            closePopup();
          },
        },
      );
    };

    if (newPhone !== user.phone) {
      showMobileVerificationPopup({
        mobile: newPhone,
        handleSubmit: updateUser,
      });

      return;
    }

    await updateUser();
  };

  const handleTutorValidation = (field: string, value: string) => {
    if (value) {
      const errMsg: string = tutorFieldsDefinition.filter(item => item.field === field)[0].error;
      const fieldToSet = `${field}Err`;
      const validation = validate(value, field as 'tutorCountryId' | 'phone' | 'tutorFullName');
      setErrorTutorMessage(error => ({
        ...error,
        [fieldToSet]: validation ? '' : errMsg,
      }));
      setIsErrorInFields(!validation);
    }
    const newUser = structuredClone(userEdit) as ITutorData;
    const parsedField = field;
    newUser[parsedField as 'tutorFullName' | 'tutorCountryId' | 'phone'] = value;
    setUserEdit(newUser);
  };

  const tutorFieldsDefinition = [
    {
      field: 'tutorFullName',
      error: 'errors.noNumbersSpecCharsAllowedMax50',
      onChange: (e: string) => handleTutorValidation('tutorFullName', e),
      title: 'tutorFullName',
      value: userEdit.tutorFullName ?? '',
    },
    {
      field: 'phone',
      error: 'errors.phoneInvalid',
      onChange: (e: string) => handleTutorValidation('phone', e),
      title: 'phoneno',
      value: userEdit.phone ?? '',
    },
    {
      field: 'tutorCountryId',
      error: 'errors.tutorCountryIdInvalid',
      onChange: (e: string) => handleTutorValidation('tutorCountryId', e),
      title: 'tutorCountryId',
      value: userEdit.tutorCountryId ?? '',
    },
    {
      field: 'email',
      error: 'errors.emailInvalid',
      onChange: (e: string) => handleTutorValidation('email', e),
      title: 'email',
      value: userEdit.email ?? '',
    }
  ];

  const areNewFieldsDifferent =
    userEdit.tutorFullName !== userBackup.tutorFullName ||
    userEdit.tutorCountryId !== userBackup.tutorCountryId ||
    userEdit.phone !== userBackup.phone ||
    userEdit.email !== userBackup.email;

  return {
    isEditing,
    setIsEditing,
    isSaveTutorButtonDisabled: isErrorInFields || !areNewFieldsDifferent,
    errorTutorMessage,
    handleStartEdit,
    handleCancelEdit,
    handleUpdateUser: handleUpdateUserInfo,
    tutorFieldsDefinition,
  };
};
